import { useEffect, useState } from "react";
import api from "./api";
import { SERVICES, ADDITIONAL_SERVICES, VEHICLE_TYPES } from "./services";
import { useSiteContent } from "../contexts/SiteContentContext";

/**
 * Hook that returns the LIVE pricing config from the backend, with the static
 * `services.js` defaults as fallback (so the UI is never empty even if the
 * /api/pricing/public request is in-flight or fails). Also overlays per-service
 * text overrides from site_text_config.services_items (admin-editable bullets).
 *
 * Returns an object with:
 *  - services: same shape as SERVICES (classic / detailing × exterior / interior / complete)
 *    Prices+durations from live pricing API, tagline/features/detailFeatures from
 *    admin overrides (when set, otherwise from static services.js).
 *  - additionalServices, vehicleSurcharges, vehicleTypes, loading
 */
export function usePricing() {
  const [data, setData] = useState(() => buildFallback());
  const [loading, setLoading] = useState(true);
  const { text } = useSiteContent();
  const items = text?.services_items;

  useEffect(() => {
    let cancelled = false;
    api.get("/pricing/public")
      .then((r) => {
        if (cancelled || !r.data) return;
        setData(mergeWithStatic(r.data));
      })
      .catch(() => { /* fallback already in state */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Overlay text overrides on the merged services tree
  const services = applyTextOverrides(data.services, items);
  return { ...data, services, loading };
}

function applyTextOverrides(services, items) {
  if (!items || !services) return services;
  const out = JSON.parse(JSON.stringify(services));
  const map = {
    classic_exterior: ["classic", "exterior"],
    classic_interior: ["classic", "interior"],
    classic_complete: ["classic", "complete"],
    detailing_exterior: ["detailing", "exterior"],
    detailing_interior: ["detailing", "interior"],
    detailing_complete: ["detailing", "complete"],
  };
  Object.entries(map).forEach(([key, [tier, slot]]) => {
    const ov = items[key];
    const target = out[tier]?.[slot];
    if (!ov || !target) return;
    if (typeof ov.tagline === "string" && ov.tagline.trim()) {
      target.tagline = ov.tagline.trim();
    }
    if (Array.isArray(ov.features) && ov.features.length > 0) {
      target.features = ov.features.slice();
    }
    if (Array.isArray(ov.detailFeatures) && ov.detailFeatures.length > 0) {
      target.detailFeatures = ov.detailFeatures.slice();
    }
  });
  return out;
}

function buildFallback() {
  const services = JSON.parse(JSON.stringify(SERVICES));
  const additionalServices = [...ADDITIONAL_SERVICES];
  const vehicleSurcharges = VEHICLE_TYPES.reduce((acc, v) => {
    acc[v.id] = v.surcharge;
    return acc;
  }, {});
  return {
    services,
    additionalServices,
    vehicleSurcharges,
    vehicleTypes: VEHICLE_TYPES,
  };
}

function mergeWithStatic(live) {
  // Merge live prices/durations into the static services tree so the marketing
  // copy (features, taglines, images) survives.
  const services = JSON.parse(JSON.stringify(SERVICES));
  for (const tier of ["classic", "detailing"]) {
    const liveTier = (live.services || {})[tier] || {};
    for (const st of ["exterior", "interior", "complete"]) {
      const liveSvc = liveTier[st];
      if (liveSvc && services[tier]?.[st]) {
        if (typeof liveSvc.price === "number") services[tier][st].price = liveSvc.price;
        if (typeof liveSvc.duration === "number") services[tier][st].duration = liveSvc.duration;
      }
    }
  }

  // additionalServices: backend returns a map { id: {name, price} }. Convert to
  // array form, preserving the order from the static list where possible (so
  // newly added items show up at the end, not in random Mongo order).
  const liveExtras = live.additionalServices || {};
  const seen = new Set();
  const additionalServices = [];
  for (const item of ADDITIONAL_SERVICES) {
    if (liveExtras[item.id]) {
      additionalServices.push({ id: item.id, name: liveExtras[item.id].name, price: liveExtras[item.id].price });
      seen.add(item.id);
    }
  }
  for (const [id, row] of Object.entries(liveExtras)) {
    if (!seen.has(id)) additionalServices.push({ id, name: row.name, price: row.price });
  }

  const vehicleSurcharges = { ...(live.vehicleSurcharges || {}) };
  const vehicleTypes = VEHICLE_TYPES.map((v) => ({
    ...v,
    surcharge: typeof vehicleSurcharges[v.id] === "number" ? vehicleSurcharges[v.id] : v.surcharge,
  }));

  return { services, additionalServices, vehicleSurcharges, vehicleTypes };
}
